// client/src/coms/voip/screenShareAudioManager.js
import { Track } from 'livekit-client';

function str(v) {
  return String(v == null ? '' : v).trim();
}

function isTauriRuntime() {
  try {
    return !!(window.__TAURI__ && window.__TAURI__.core && typeof window.__TAURI__.core.invoke === 'function');
  } catch {
    return false;
  }
}

function isChromiumLike() {
  try {
    const ua = String(window?.navigator?.userAgent || '').toLowerCase();
    if (!ua) return false;
    return ua.includes('chrome') || ua.includes('chromium') || ua.includes('edg/') || ua.includes('electron');
  } catch {
    return false;
  }
}

function canHintSystemAudio() {
  return isTauriRuntime() || isChromiumLike();
}

function normalizeTrackSource(source) {
  return str(source).toLowerCase().replace(/[^a-z0-9]+/g, '');
}

function getTrackId(track) {
  const t = track || null;
  if (!t) return '';

  try {
    const mediaId = str(t.id || '');
    if (mediaId) return mediaId;
  } catch {}

  try {
    const sid = str(t.sid || '');
    if (sid) return sid;
  } catch {}

  return '';
}

function isUsableAudioTrack(track) {
  const t = track || null;
  if (!t) return false;

  try {
    if (t.kind && Track && Track.Kind && t.kind !== Track.Kind.Audio && t.kind !== 'audio') return false;
  } catch {}

  try {
    if (t.readyState && String(t.readyState) === 'ended') return false;
  } catch {}

  return true;
}

function buildScreenShareAudioConstraints() {
  return {
    echoCancellation: false,
    noiseSuppression: false,
    autoGainControl: false,
    channelCount: { ideal: 2 },
    sampleRate: { ideal: 48000 },
    sampleSize: { ideal: 16 },
    latency: { ideal: 0.01 },
  };
}

async function tryApplyScreenShareAudioConstraints(track) {
  const t = track || null;
  if (!t || typeof t.applyConstraints !== 'function') return;

  try {
    await t.applyConstraints(buildScreenShareAudioConstraints());
  } catch {}
}

export function getTrackSourceValue(track, pub = null) {
  try {
    if (pub && pub.source != null) return str(pub.source);
  } catch {}

  try {
    if (pub && pub.track && pub.track.source != null) return str(pub.track.source);
  } catch {}

  try {
    if (track && track.source != null) return str(track.source);
  } catch {}

  return '';
}

export function getScreenShareAudioSource() {
  try {
    if (Track && Track.Source && Track.Source.ScreenShareAudio != null) return Track.Source.ScreenShareAudio;
  } catch {}
  return 'screen_share_audio';
}

export function isScreenShareAudioSourceValue(source) {
  const normalized = normalizeTrackSource(source);
  if (!normalized) return false;
  if (normalized === 'screenshareaudio') return true;
  if (normalized.includes('screen') && normalized.includes('audio')) return true;
  return false;
}

export function isScreenShareVideoSourceValue(source) {
  const normalized = normalizeTrackSource(source);
  if (!normalized) return false;
  if (normalized === 'screenshare') return true;
  if (normalized === 'screensharevideo') return true;
  if (normalized === 'screen') return true;
  if (normalized.includes('screen') && !normalized.includes('audio')) return true;
  return false;
}

export function isVoiceAudioSourceValue(source) {
  const normalized = normalizeTrackSource(source);
  if (!normalized) return false;
  if (normalized === 'microphone') return true;
  if (normalized === 'mic') return true;
  if (normalized.includes('microphone')) return true;
  if (normalized.includes('mic')) return true;
  if (normalized.includes('voice')) return true;
  return false;
}

export function isScreenShareAudioTrack(track, pub = null) {
  const source = getTrackSourceValue(track, pub);
  if (!source) return false;

  try {
    const screenAudioSource = getScreenShareAudioSource();
    if (screenAudioSource != null && String(screenAudioSource) === String(source)) return true;
  } catch {}

  return isScreenShareAudioSourceValue(source);
}

export function getParticipantAudioPublications(participant, { includeMuted = false, requireTrack = false } = {}) {
  const p = participant || null;
  if (!p) return [];

  try {
    const pubs = Array.from(p.audioTrackPublications?.values() || []);
    return pubs.filter((pub) => {
      if (!pub) return false;
      if (!includeMuted && pub.isMuted) return false;
      if (requireTrack && !pub.track) return false;
      return true;
    });
  } catch {}

  return [];
}

export function hasParticipantScreenTrack(participant) {
  const p = participant || null;
  if (!p) return false;

  try {
    const pubs = Array.from(p.videoTrackPublications?.values() || []);
    for (const pub of pubs) {
      if (!pub || pub.isMuted) continue;
      const source = getTrackSourceValue(pub.track || null, pub);
      if (isScreenShareVideoSourceValue(source)) return true;
    }
  } catch {}

  return false;
}

export function pickParticipantVoiceAudioPublication(participant) {
  const pubs = getParticipantAudioPublications(participant, { includeMuted: false, requireTrack: true });
  if (!pubs.length) return null;

  for (const pub of pubs) {
    const source = getTrackSourceValue(pub.track || null, pub);
    if (isVoiceAudioSourceValue(source)) return pub;
  }

  for (const pub of pubs) {
    const source = getTrackSourceValue(pub.track || null, pub);
    if (!isScreenShareAudioSourceValue(source)) return pub;
  }

  return pubs[0] || null;
}

export function pickParticipantScreenAudioPublication(participant) {
  const pubs = getParticipantAudioPublications(participant, { includeMuted: false, requireTrack: true });
  if (!pubs.length) return null;

  for (const pub of pubs) {
    const source = getTrackSourceValue(pub.track || null, pub);
    if (isScreenShareAudioSourceValue(source)) return pub;
  }

  if (!hasParticipantScreenTrack(participant)) return null;
  if (pubs.length < 2) return null;

  const voicePub = pickParticipantVoiceAudioPublication(participant);
  for (const pub of pubs) {
    if (!pub) continue;
    if (voicePub && pub === voicePub) continue;
    return pub;
  }

  return null;
}

export function isScreenShareAudioPublication(participantOrPub, maybePub = null) {
  const participant = maybePub != null ? participantOrPub : null;
  const candidate = maybePub != null ? maybePub : participantOrPub;
  if (!candidate) return false;

  const source = getTrackSourceValue(candidate && candidate.track ? candidate.track : null, candidate);
  if (isScreenShareAudioSourceValue(source)) return true;

  if (!participant) return false;

  const picked = pickParticipantScreenAudioPublication(participant);
  if (!picked) return false;

  try {
    if (picked === candidate) return true;
  } catch {}

  const pickedSid = str(picked.trackSid || picked.sid || (picked.track && picked.track.sid) || '');
  const candidateSid = str(candidate.trackSid || candidate.sid || (candidate.track && candidate.track.sid) || '');
  if (pickedSid && candidateSid && pickedSid === candidateSid) return true;

  return false;
}

export function isVoiceAudioPublication(participant, pub) {
  const candidate = pub || null;
  if (!candidate) return false;

  const picked = pickParticipantVoiceAudioPublication(participant);
  if (!picked) return false;

  try {
    if (picked === candidate) return true;
  } catch {}

  const pickedSid = str(picked.trackSid || picked.sid || (picked.track && picked.track.sid) || '');
  const candidateSid = str(candidate.trackSid || candidate.sid || (candidate.track && candidate.track.sid) || '');
  if (pickedSid && candidateSid && pickedSid === candidateSid) return true;

  return false;
}

export function pickParticipantScreenAudioTrack(participant) {
  const pub = pickParticipantScreenAudioPublication(participant);
  if (!pub || pub.isMuted) return null;

  const t = pub.track || null;
  if (!t) return null;

  try {
    if (t.kind && Track && Track.Kind && t.kind !== Track.Kind.Audio && t.kind !== 'audio') return null;
  } catch {}

  if (t.mediaStreamTrack) return t;
  return null;
}

export function ensureScreenShareAudioTrackOnStream(stream, audioTrack) {
  const s = stream || null;
  const t = audioTrack || null;
  if (!s || !t) return false;
  if (typeof s.getAudioTracks !== 'function' || typeof s.addTrack !== 'function') return false;

  try {
    const hasAudio = Array.from(s.getAudioTracks() || []).some((existing) => {
      if (!existing || !t) return false;
      if (existing === t) return true;
      const eid = getTrackId(existing);
      const tid = getTrackId(t);
      return !!eid && !!tid && eid == tid;
    });
    if (!hasAudio) s.addTrack(t);
    return true;
  } catch {
    return false;
  }
}

export function getActiveScreenShareAudioTrackCount(stream) {
  const s = stream || null;
  if (!s || typeof s.getAudioTracks !== 'function') return 0;

  try {
    return Array.from(s.getAudioTracks() || []).filter((track) => isUsableAudioTrack(track)).length;
  } catch {}

  return 0;
}

export function pickPreferredScreenShareAudioTrackFromStream(stream) {
  const s = stream || null;
  if (!s || typeof s.getAudioTracks !== 'function') return null;

  try {
    const tracks = Array.from(s.getAudioTracks() || []).filter(Boolean);
    if (!tracks.length) return null;

    const usable = tracks.filter((track) => isUsableAudioTrack(track));
    if (usable.length) return usable[0];

    return tracks[0] || null;
  } catch {}

  return null;
}

export function buildScreenShareAudioPlaybackStream({ p2pStream = null, participant = null } = {}) {
  try {
    const p2pTrack = pickPreferredScreenShareAudioTrackFromStream(p2pStream);
    if (p2pTrack) {
      const ms = new MediaStream();
      ms.addTrack(p2pTrack);
      if (typeof ms.getAudioTracks === 'function' && ms.getAudioTracks().length) return ms;
    }
  } catch {}

  const audioTrack = pickParticipantScreenAudioTrack(participant);
  if (!audioTrack || !audioTrack.mediaStreamTrack) return null;

  try {
    const ms = new MediaStream();
    ms.addTrack(audioTrack.mediaStreamTrack);
    return ms;
  } catch {
    return null;
  }
}

export function canUseScreenShareSystemAudio() {
  try {
    return !!(navigator.mediaDevices && typeof navigator.mediaDevices.getDisplayMedia === 'function');
  } catch {
    return false;
  }
}

export function buildScreenShareCaptureConstraints({ width, height, fps } = {}) {
  const video = {
    width: width != null ? { ideal: width } : undefined,
    height: height != null ? { ideal: height } : undefined,
    frameRate: fps != null ? { ideal: fps, max: fps } : undefined,
  };

  if (!canUseScreenShareSystemAudio()) {
    return {
      video,
      audio: false,
    };
  }

  const out = {
    video,
    audio: buildScreenShareAudioConstraints(),
  };

  if (canHintSystemAudio()) {
    out.systemAudio = 'include';
    out.windowAudio = 'system';
    out.selfBrowserSurface = 'exclude';
    out.surfaceSwitching = 'include';
  }

  return out;
}

export async function captureScreenShareMedia({ width, height, fps } = {}) {
  if (!navigator.mediaDevices || typeof navigator.mediaDevices.getDisplayMedia !== 'function') {
    throw new Error('screen_share_unsupported');
  }

  const stream = await navigator.mediaDevices.getDisplayMedia(
    buildScreenShareCaptureConstraints({ width, height, fps })
  );

  const videoTrack = stream && stream.getVideoTracks ? stream.getVideoTracks()[0] : null;
  const audioTrack = stream && stream.getAudioTracks
    ? (stream.getAudioTracks()[0] || null)
    : null;

  if (!videoTrack) {
    try {
      const tracks = Array.from(stream?.getTracks?.() || []);
      for (const t of tracks) {
        try {
          t.stop();
        } catch {}
      }
    } catch {}
    throw new Error('screen_track_missing');
  }

  try {
    videoTrack.contentHint = 'detail';
  } catch {}

  try {
    await tryApplyScreenShareAudioConstraints(audioTrack);
  } catch {}

  return {
    stream,
    track: videoTrack,
    audioTrack,
    hasAudio: !!audioTrack,
  };
}
